import CloseOutlined from '@ant-design/icons/CloseOutlined'
import FileTextOutlined from '@ant-design/icons/FileTextOutlined'

import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import { alpha, keyframes } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import IconButton from '@components/_mui/IconButton'
import Typography from '@components/_mui/Typography'

import DocTitle from '@components/DocTitle'
import LinearProgress from '@components/LinearProgress'

import SOPDocument from './SOPsDialog/SOPDocument'
import { useSOP, useSOPCreate, useSOPDelete, useSOPEdit } from './SOPsDialog/SOPsDialog.hooks'
import { useSOPsFilters } from './SOPsList'
import SOPsSidebar from './SOPsSidebar'

export default function SOPsWorkspace({ open, onClose }) {
  return (
    <Dialog fullScreen open={open} onClose={onClose} slotProps={{ paper: { sx: styles.dialogPaper } }} data-testid="sops-workspace">
      <DocTitle title="SOPs" />
      <Workspace onClose={onClose} />
    </Dialog>
  )
}

function Workspace({ onClose }) {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'))
  const [{ sops, sopId }] = useSOPsFilters()
  // Mirror DocumentPane's notion of "something to show" so an incomplete `?sops=view` URL (no
  // sopId) never hides the sidebar on mobile and strands the user on an empty pane.
  const docOpen = sops === 'create' || (sops === 'view' && Boolean(sopId))

  const showSidebar = !isMobile || !docOpen
  const showDoc = !isMobile || docOpen

  return (
    <Stack sx={styles.shell}>
      <TopBar onClose={onClose} />
      <Box sx={styles.body}>
        {showSidebar && (
          <Box sx={styles.sidebar}>
            <SOPsSidebar />
          </Box>
        )}
        {showDoc && (
          <Box sx={styles.docArea}>
            <Paper elevation={0} sx={styles.bubble}>
              <DocumentPane isMobile={isMobile} />
            </Paper>
          </Box>
        )}
      </Box>
    </Stack>
  )
}

function TopBar({ onClose }) {
  return (
    <Stack direction="row" sx={styles.topBar}>
      <Box sx={styles.brandIcon}>
        <FileTextOutlined style={{ fontSize: 16 }} />
      </Box>
      <Typography variant="h4">SOPs</Typography>
      <Box sx={{ flex: 1 }} />
      <IconButton color="secondary" onClick={onClose} data-testid="close-sops">
        <CloseOutlined />
      </IconButton>
    </Stack>
  )
}

function DocumentPane({ isMobile }) {
  const [{ sops, sopId }, updateFilters] = useSOPsFilters()

  const isCreate = sops === 'create'
  const isView = sops === 'view' && Boolean(sopId)
  const hasSelection = isCreate || isView

  const { data, isPending } = useSOP(sopId, { enabled: isView })

  const create = useSOPCreate()
  const edit = useSOPEdit(sopId)
  const remove = useSOPDelete(sopId)

  // While a saved SOP loads, show a thin bar pinned to the top of the bubble (the pane is
  // position:relative) rather than a centered spinner.
  const loading = isView && (isPending || !data)

  const backToList = () => updateFilters({ sops: 'list', sopId: undefined })

  const handleSave = (payload) =>
    isCreate ? create.mutateAsync(payload).then((created) => updateFilters({ sops: 'view', sopId: created.id })) : edit.mutateAsync(payload)

  const handleDelete = () => remove.mutateAsync().then(backToList)

  if (!hasSelection) return <EmptyDocument />

  return (
    <>
      <LinearProgress loading={loading} />
      {!loading && (
        <SOPDocument
          key={sopId ?? 'create'}
          isCreate={isCreate}
          data={data}
          showBack={isMobile}
          onSave={handleSave}
          onDelete={handleDelete}
          onBack={backToList}
        />
      )}
    </>
  )
}

function EmptyDocument() {
  return (
    <Stack sx={styles.empty} data-testid="sops-empty-document">
      <Box sx={styles.emptyIcon}>
        <FileTextOutlined style={{ fontSize: 30 }} />
      </Box>
      <Typography variant="h4" sx={{ color: 'text.secondary' }}>
        Select a document to start reading
      </Typography>
      <Typography variant="body2" sx={styles.emptySubtext}>
        Browse or search the list on the left, then open any SOP to read it here.
      </Typography>
    </Stack>
  )
}

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const styles = {
  dialogPaper: {
    bgcolor: 'background.default',
  },
  shell: {
    height: '100%',
    minHeight: 0,
  },
  topBar: {
    flex: '0 0 auto',
    minHeight: 54,
    alignItems: 'center',
    gap: 1.5,
    px: 2,
  },
  brandIcon: {
    width: 28,
    height: 28,
    borderRadius: 1.5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'primary.main',
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
  },
  body: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
  },
  sidebar: {
    flex: { xs: '1 1 auto', md: '0 0 442px' },
    width: { md: 442 },
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  docArea: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    p: { xs: 1, md: 1.5 },
    pl: { md: 0.5 },
  },
  bubble: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    bgcolor: 'background.paper',
    borderRadius: { xs: '14px', md: '20px' },
    boxShadow: (theme) => theme.customShadows.z1,
    overflow: 'hidden',
  },
  empty: {
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: 1.5,
    p: 4,
    animation: `${fadeIn} 240ms ease`,
  },
  emptyIcon: {
    width: 64,
    height: 64,
    borderRadius: '18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'text.disabled',
    bgcolor: 'grey.100',
  },
  emptySubtext: {
    color: 'text.disabled',
    maxWidth: 320,
    textAlign: 'center',
  },
}
